import {memo, useContext, useState} from 'react';
import {QueryKey, useMutation, useQueryClient} from '@tanstack/react-query';
import {AxiosError} from 'axios';
import Switch from 'patient-ping-remedy/packages/switch';
import {User} from '@/api/dto/dto';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useAuthStore} from '@/store/auth_store';
import {GENERICERROR} from '@manual_encounters/constants/BannerMessages';
import Helpers from '@/helpers/helpers';

type PatchUserParams = {
  userId: string;
  onlyShowUnread: boolean;
}

type Props = {
  queryKey(newCurrentUser: User): QueryKey;
  currentFetchedUser: User;
  property: 'only_show_unread_notifications' | 'only_show_unread_messages';
  label: string;
}

function WidgetOnlyShowUnread({ queryKey, currentFetchedUser, property, label }: Props) {
  const { carecoApi } = useContext(CarecoApiContext);
  const queryClient = useQueryClient();
  const setCurrentUser = useAuthStore((state) => state.setCurrentUser);
  const [disableShowUnreadSwitch, setDisableShowUnreadSwitch] = useState(false);

  const { mutate } = useMutation({
    mutationFn: (params: PatchUserParams) =>
      carecoApi!.patchUser(params.userId, { [property]: params.onlyShowUnread }),
    onSuccess(newCurrentUser, params) {
      setDisableShowUnreadSwitch(false);
      setCurrentUser({ ...currentFetchedUser, [property]: params.onlyShowUnread });

      void queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      void queryClient.invalidateQueries({ queryKey: queryKey(newCurrentUser) });
    },
    onError(error: AxiosError) {
      setDisableShowUnreadSwitch(false);
      Helpers.alertOnError(`Failed to update show unread ${label} setting. ${GENERICERROR} ${Helpers.traceId(error)}`, error);
    },
  });

  const updateUnreadOnly = async () => {
    if (currentFetchedUser) {
      setDisableShowUnreadSwitch(true);
      mutate({
        userId: currentFetchedUser.id,
        onlyShowUnread: currentFetchedUser[property],
      });
    }
  };

  return (
    <Switch
      label="Only show unread"
      onChange={(e)=> {
        e.preventDefault();
        currentFetchedUser[property] = !currentFetchedUser[property];
        void updateUnreadOnly();
      }}
      checked={currentFetchedUser[property]}
      disabled={disableShowUnreadSwitch}
    />
  );
}

export default memo(WidgetOnlyShowUnread);
